import { errorMessageFrom } from "@moeru/std";
import { nanoid } from "nanoid";
import { rawTool } from "@xsai/tool";
import { toJsonSchema, validate } from "xsschema";
import { z } from "zod";
//#region src/agents/spark-notify/event-source.ts
function formatMetadataSource(source) {
	if (!source) return void 0;
	if ("extension" in source) return `${source.extension.id}:${source.id}`;
	return source.id;
}
/**
* Resolves a stable source key for websocket-originated events.
*
* Before:
* - `{ source: "minecraft" }`
* - `{ metadata: { source: { extension: { id: "p" }, id: "i" } } }`
*
* After:
* - `"minecraft"`
* - `"p:i"`
*/
function getEventSourceKey(event, fallback = "unknown") {
	return formatMetadataSource(event.metadata?.source) ?? event.source ?? fallback;
}
//#endregion
//#region src/agents/spark-notify/schema.ts
const JSON_SCHEMA_NULLABLE_SCALAR_TYPES = new Set([
	"string",
	"number",
	"integer",
	"boolean",
	"null"
]);
function isJsonSchema(value) {
	return Boolean(value && !Array.isArray(value) && typeof value === "object");
}
/**
* Normalizes nullable scalar unions in generated JSON schema.
*
* Before:
* - `{ anyOf: [{ type: 'string' }, { type: 'null' }] }`
*
* After:
* - `{ type: ['string', 'null'] }`
*/
function normalizeNullableAnyOf(schema) {
	const next = { ...schema };
	if (next.properties) next.properties = Object.fromEntries(Object.entries(next.properties).map(([key, value]) => {
		if (!isJsonSchema(value)) return [key, value];
		return [key, normalizeNullableAnyOf(value)];
	}));
	if (Array.isArray(next.items)) next.items = next.items.map((item) => isJsonSchema(item) ? normalizeNullableAnyOf(item) : item);
	else if (isJsonSchema(next.items)) next.items = normalizeNullableAnyOf(next.items);
	if (next.anyOf) {
		next.anyOf = next.anyOf.map((value) => isJsonSchema(value) ? normalizeNullableAnyOf(value) : value);
		const normalizedEntries = next.anyOf.filter(isJsonSchema);
		const primitiveTypes = normalizedEntries.map((entry) => entry.type).filter((type) => typeof type === "string");
		const dedupedPrimitiveTypes = [...new Set(primitiveTypes)];
		if (primitiveTypes.length === normalizedEntries.length && dedupedPrimitiveTypes.length > 0 && dedupedPrimitiveTypes.every((type) => JSON_SCHEMA_NULLABLE_SCALAR_TYPES.has(String(type)))) {
			delete next.anyOf;
			next.type = dedupedPrimitiveTypes;
		}
	}
	if (next.oneOf) next.oneOf = next.oneOf.map((value) => isJsonSchema(value) ? normalizeNullableAnyOf(value) : value);
	return next;
}
const sparkCommandGuidanceOptionSchema = z.object({
	label: z.string().describe("Short label for the option."),
	steps: z.array(z.string()).min(1).describe("Step-by-step actions the target should follow."),
	rationale: z.union([z.string(), z.null()]).describe("Why this option makes sense."),
	possibleOutcome: z.union([z.array(z.string()), z.null()]).describe("Expected outcomes if this option is followed."),
	risk: z.union([z.enum([
		"high",
		"medium",
		"low",
		"none"
	]), z.null()]).describe("Risk level of this option."),
	fallback: z.union([z.array(z.string()), z.null()]).describe("Fallback steps if the main plan fails."),
	triggers: z.union([z.array(z.string()), z.null()]).describe("Conditions that should trigger this option.")
}).strict();
const sparkCommandPersonaSchema = z.object({
	traits: z.string().describe("Trait name to adjust behavior. For example, \"bravery\", \"cautiousness\", \"friendliness\"."),
	strength: z.enum([
		"very-high",
		"high",
		"medium",
		"low",
		"very-low"
	])
}).strict();
const sparkNotifyCommandGuidanceSchema = z.object({
	type: z.enum([
		"proposal",
		"instruction",
		"memory-recall"
	]),
	persona: z.union([z.array(sparkCommandPersonaSchema), z.null()]).describe("Optional persona controls for the receiver."),
	options: z.array(sparkCommandGuidanceOptionSchema)
}).strict();
const sparkNotifyCommandItemSchema = z.object({
	destinations: z.array(z.string()).min(1).describe("List of sub-agent IDs to send the command to"),
	interrupt: z.union([z.enum([
		"force",
		"soft",
		"false"
	]), z.null()]).describe("Interrupt type: force, soft, or false (no interrupt)."),
	priority: z.union([z.enum([
		"critical",
		"high",
		"normal",
		"low"
	]), z.null()]).describe("Semantic priority of the command."),
	intent: z.union([z.enum([
		"plan",
		"proposal",
		"action",
		"pause",
		"resume",
		"reroute",
		"context"
	]), z.null()]).describe("Intent of the command."),
	ack: z.string().describe("Acknowledgment content used to be passed to sub-agents upon command receipt."),
	guidance: z.union([sparkNotifyCommandGuidanceSchema, z.null()]).describe("Guidance for the sub-agent on how to interpret and execute the command.")
}).strict();
const sparkNotifyCommandSchema = z.object({ commands: z.array(sparkNotifyCommandItemSchema).describe("List of commands to issue to sub-agents. Empty array can be used for zero commands.") }).strict();
//#endregion
//#region src/agents/spark-notify/tools.ts
/**
* Normalizes provider-facing notify command payloads into websocket draft commands.
*
* Use when:
* - LLM output has been validated against `sparkNotifyCommandSchema`
* - You need `spark:command`-compatible draft objects
*
* Expects:
* - Input shape from one `commands[]` entry
*
* Returns:
* - Runtime-ready command draft for downstream emitters
*/
function normalizeSparkNotifyCommand(command) {
	return {
		destinations: command.destinations,
		guidance: command.guidance ? {
			type: command.guidance.type,
			persona: command.guidance.persona?.reduce((acc, curr) => {
				acc[curr.traits] = curr.strength;
				return acc;
			}, {}) || void 0,
			options: command.guidance.options.map((option) => ({
				...option,
				rationale: option.rationale ?? void 0,
				possibleOutcome: option.possibleOutcome?.length ? option.possibleOutcome : void 0,
				risk: option.risk ?? void 0,
				fallback: option.fallback?.length ? option.fallback : void 0,
				triggers: option.triggers?.length ? option.triggers : void 0
			}))
		} : void 0,
		contexts: [],
		priority: command.priority || "normal",
		intent: command.intent || "action",
		ack: command.ack || void 0,
		interrupt: command.interrupt === "false" || command.interrupt == null ? false : command.interrupt
	};
}
/**
* Creates built-in tools used by the Spark Notify agent.
*
* Use when:
* - Running the Spark Notify agent on any runtime (web, desktop, eval harness)
* - You need "no response" and "command draft" tool pathways
*
* Expects:
* - Callbacks for command collection and no-response signaling
*
* Returns:
* - Tool array consumable by `@xsai/stream-text`
*/
async function createSparkNotifyTools(options) {
	const allowNoResponse = options.allowNoResponse !== false;
	const allowSparkCommand = options.allowSparkCommand !== false;
	const tools = [];
	const sparkNoResponseTool = rawTool({
		name: "builtIn_sparkNoResponse",
		description: "Indicate that no response or action is needed for the current spark:notify event.",
		parameters: normalizeNullableAnyOf(await toJsonSchema(z.object({}).strict())),
		execute: async (_rawPayload, context) => {
			options.onTrace?.({
				type: "model-output-tool-call",
				payload: {
					toolName: "builtIn_sparkNoResponse",
					toolCallId: context?.toolCallId
				}
			});
			options.onNoResponse();
			options.onTrace?.({
				type: "tool-execution",
				payload: {
					toolName: "builtIn_sparkNoResponse",
					toolCallId: context?.toolCallId,
					responseMode: "no-response"
				}
			});
			return "AIRI System: Acknowledged, no response or action will be processed.";
		}
	});
	if (allowNoResponse) tools.push(sparkNoResponseTool);
	const sparkCommandTool = rawTool({
		name: "builtIn_sparkCommand",
		description: "Issue a spark:command to sub-agents. You can call this tool multiple times.",
		parameters: normalizeNullableAnyOf(await toJsonSchema(sparkNotifyCommandSchema)),
		execute: async (rawPayload, context) => {
			options.onTrace?.({
				type: "model-output-tool-call",
				payload: {
					toolName: "builtIn_sparkCommand",
					toolCallId: context?.toolCallId,
					rawPayload
				}
			});
			try {
				const validated = await validate(sparkNotifyCommandSchema, rawPayload);
				options.onCommands(validated.commands.map(normalizeSparkNotifyCommand));
				options.onTrace?.({
					type: "tool-execution",
					payload: {
						toolName: "builtIn_sparkCommand",
						toolCallId: context?.toolCallId,
						commandCount: validated.commands.length
					}
				});
			} catch (error) {
				options.onTrace?.({
					type: "tool-execution",
					payload: {
						toolName: "builtIn_sparkCommand",
						toolCallId: context?.toolCallId,
						ok: false,
						error: errorMessageFrom(error)
					}
				});
				return `AIRI System: Error - invalid spark_command parameters: ${errorMessageFrom(error)}`;
			}
			return "AIRI System: Acknowledged, command fired.";
		}
	});
	if (allowSparkCommand) tools.push(sparkCommandTool);
	return { tools };
}
//#endregion
//#region src/agents/spark-notify/handler.ts
/**
* Serializes one spark-notify payload into the user message content sent to the model.
*
* Use when:
* - A runtime needs the default JSON envelope for spark-notify
* - A host optionally appends one-off serialized context sections for the current run
*
* Expects:
* - `messageOverride` content to already be provider-safe text
*
* Returns:
* - A single provider-ready user message string
*/
function renderSparkNotifyUserMessage(input) {
	if (input.messageOverride?.replaceUserMessage) return input.messageOverride.replaceUserMessage;
	return [JSON.stringify({
		notify: input.event.data,
		source: input.event.source
	}, null, 2), ...input.messageOverride?.appendUserSections ?? []].filter((section) => section.trim().length > 0).join("\n\n");
}
/**
* Builds the instruction block prepended to Spark Notify agent prompts.
*
* Use when:
* - Handling `spark:notify` events
* - Constructing the per-turn system instruction for the notify reaction agent
*
* Expects:
* - `moduleName` resolved from event source metadata
*
* Returns:
* - Multiline instruction text for system prompt composition
*/
function getSparkNotifyHandlingAgentInstruction(moduleName) {
	return [
		"This is AIRI system, the life pod hosting your consciousness. You don't need to respond to me or every spark:notify event directly.",
		`Another module "${moduleName}" triggered spark:notify event for you to checkout.`,
		"You may call the built-in tool \"builtIn_sparkCommand\" to issue spark:command to sub-agents as needed.",
		"For any of the output that is not a tool call, it will be streamed to user's interface and maybe processed with text to speech system ",
		"to be played out loud as your actual reaction to the spark:notify event."
	].join("\n");
}
function resolveSparkNotifyRuntimePolicy(control) {
	if (control?.forceTextResponse && control?.forceSparkCommandResponse) console.warn("[spark:notify] forceTextResponse and forceSparkCommandResponse were both set; preferring forceTextResponse");
	if (control?.forceTextResponse) return {
		allowNoResponse: false,
		allowSparkCommand: false,
		supportsTools: false,
		waitForTools: false,
		ignoreTextOutput: false
	};
	if (control?.forceSparkCommandResponse) return {
		allowNoResponse: false,
		allowSparkCommand: true,
		supportsTools: true,
		waitForTools: true,
		toolChoice: {
			type: "function",
			function: { name: "builtIn_sparkCommand" }
		},
		ignoreTextOutput: true
	};
	if (control?.forceResponse) return {
		allowNoResponse: false,
		allowSparkCommand: true,
		supportsTools: true,
		waitForTools: true,
		ignoreTextOutput: false
	};
	return {
		allowNoResponse: true,
		allowSparkCommand: true,
		supportsTools: true,
		waitForTools: true,
		ignoreTextOutput: false
	};
}
function traceSpark(deps, event) {
	deps.onTrace?.(event);
}
/**
* Creates a platform-agnostic Spark Notify event handler.
*
* Use when:
* - A runtime consumes websocket `spark:notify` events
* - Reactions and command drafts should be generated by an LLM with built-in tools
* - You want identical behavior across stage-ui and offline eval harnesses
*
* Expects:
* - Stream/provider adapters and state accessors passed in `deps`
*
* Returns:
* - `handle(event, control)` function that applies queue/processing policy and returns generated commands
*
* Call stack:
*
* `handle`
*   -> `runNotifyAgent`
*     -> `createSparkNotifyTools`
*       -> `deps.stream`
*         -> `deps.onReactionDelta`/`deps.onReactionEnd`
*/
function setupAgentSparkNotifyHandler(deps) {
	async function runNotifyAgent(event, control) {
		const activeProvider = deps.getActiveProvider();
		const activeModel = deps.getActiveModel();
		if (!activeProvider || !activeModel) {
			console.warn("Spark notify ignored: missing active provider or model");
			return;
		}
		const runtimePolicy = resolveSparkNotifyRuntimePolicy(control);
		const chatProvider = await deps.getProviderInstance(activeProvider);
		const commandDrafts = [];
		let noResponse = false;
		const { tools } = await createSparkNotifyTools({
			onNoResponse: () => {
				noResponse = true;
			},
			onCommands: (commands) => commandDrafts.push(...commands),
			onTrace: deps.onTrace,
			allowNoResponse: runtimePolicy.allowNoResponse,
			allowSparkCommand: runtimePolicy.allowSparkCommand
		});
		const messages = [{
			role: "system",
			content: [
				deps.getSystemPrompt(),
				getSparkNotifyHandlingAgentInstruction(getEventSourceKey(event)),
				...control?.messageOverride?.appendSystemInstructions ?? []
			].filter(Boolean).join("\n\n")
		}, {
			role: "user",
			content: renderSparkNotifyUserMessage({
				event,
				messageOverride: control?.messageOverride
			})
		}];
		traceSpark(deps, {
			type: "messages-rendered",
			payload: {
				eventId: event.data.eventId,
				source: event.source,
				messageCount: messages.length,
				toolCount: tools.length,
				renderedMessages: messages
			}
		});
		traceSpark(deps, {
			type: "tools-prepared",
			payload: {
				eventId: event.data.eventId,
				toolNames: tools.flatMap((tool) => {
					const name = tool.function?.name;
					return name ? [name] : [];
				}),
				toolExposure: tools.flatMap((tool) => {
					const name = tool.function?.name;
					if (!name) return [];
					return [{
						name,
						description: tool.function?.description
					}];
				}),
				allowNoResponse: runtimePolicy.allowNoResponse,
				allowSparkCommand: runtimePolicy.allowSparkCommand,
				supportsTools: runtimePolicy.supportsTools,
				waitForTools: runtimePolicy.waitForTools
			}
		});
		traceSpark(deps, {
			type: "model-input",
			payload: {
				eventId: event.data.eventId,
				model: activeModel,
				provider: activeProvider,
				messages,
				toolChoice: runtimePolicy.toolChoice ?? null,
				supportsTools: runtimePolicy.supportsTools,
				waitForTools: runtimePolicy.waitForTools
			}
		});
		let fullText = "";
		await deps.stream(activeModel, chatProvider, messages, {
			tools,
			supportsTools: runtimePolicy.supportsTools,
			waitForTools: runtimePolicy.waitForTools,
			toolChoice: runtimePolicy.toolChoice,
			onStreamEvent: async (streamEvent) => {
				if (streamEvent.type === "text-delta") {
					if (runtimePolicy.ignoreTextOutput || noResponse) return;
					const nextText = `${fullText}${streamEvent.text}`;
					traceSpark(deps, {
						type: "model-output-text",
						payload: {
							eventId: event.data.id,
							text: streamEvent.text,
							accumulatedText: nextText
						}
					});
					deps.onReactionDelta(event.data.id, streamEvent.text);
					fullText = nextText;
				}
				if (streamEvent.type === "tool-call") traceSpark(deps, {
					type: "model-output-tool-call",
					payload: {
						eventId: event.data.eventId,
						kind: "tool-call",
						...streamEvent
					}
				});
				if (streamEvent.type === "tool-result") traceSpark(deps, {
					type: "tool-execution",
					payload: {
						eventId: event.data.eventId,
						kind: "tool-result",
						...streamEvent
					}
				});
				if (streamEvent.type === "finish") if (noResponse) deps.onReactionEnd(event.data.id, "");
				else deps.onReactionEnd(event.data.id, fullText);
				if (streamEvent.type === "error") {
					deps.onReactionEnd(event.data.id, fullText);
					throw streamEvent.error ?? /* @__PURE__ */ new Error("Spark notify stream error");
				}
			}
		});
		const reaction = fullText.trim();
		traceSpark(deps, {
			type: "result",
			payload: {
				eventId: event.data.eventId,
				reaction,
				commandCount: commandDrafts.length,
				noResponse,
				supportsTools: runtimePolicy.supportsTools,
				commands: commandDrafts,
				normalizedReaction: reaction,
				normalizedCommands: commandDrafts
			}
		});
		return {
			reaction,
			commands: commandDrafts
		};
	}
	async function handle(event, control) {
		if (event.data.urgency !== "immediate" && deps.getPending().length > 0) {
			deps.setPending([...deps.getPending(), event]);
			return;
		}
		if (deps.getProcessing()) {
			deps.setPending([...deps.getPending(), event]);
			return;
		}
		deps.setProcessing(true);
		try {
			const response = await runNotifyAgent(event, control);
			if (!response) return void 0;
			return { commands: (response.commands ?? []).map((command) => ({
				id: nanoid(),
				eventId: nanoid(),
				parentEventId: event.data.id,
				commandId: nanoid(),
				interrupt: (command.interrupt === true ? "force" : command.interrupt) ?? false,
				priority: command.priority ?? "normal",
				intent: command.intent ?? "action",
				ack: command.ack,
				guidance: command.guidance,
				contexts: command.contexts,
				destinations: command.destinations ?? []
			})).filter((command) => command.destinations.length > 0) };
		} finally {
			deps.setProcessing(false);
		}
	}
	return { handle };
}
//#endregion
export { createSparkNotifyTools, getSparkNotifyHandlingAgentInstruction, setupAgentSparkNotifyHandler, sparkNotifyCommandItemSchema, sparkNotifyCommandSchema };
